// Вложения задачи: файлы перетаскиваются в открытое окно (форму новой задачи или карточку задачи),
// скриншоты вставляются из буфера (⌘V / Ctrl+V), есть кнопка «Прикрепить файлы».
// Файлы достаются верхнему открытому окну: если карточка открыта под формой — их получит форма.
import { useEffect, useId, useRef, useState } from 'react'
import { formatSize } from '../lib/format.ts'
import type { AttachmentInfo } from '../types.ts'
import { Icon } from './Icon.tsx'
import { Button, cx } from './ui.tsx'

export const MAX_FILE_BYTES = 100 * 1024 * 1024

type Receiver = (files: File[]) => void
const receivers: Receiver[] = []
const dragListeners = new Set<(v: boolean) => void>()
let depth = 0
let installed = false

const hasFiles = (e: DragEvent): boolean => Boolean(e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files'))
const setDragging = (v: boolean) => dragListeners.forEach((f) => f(v))

/** Скриншот из буфера приходит как «image.png» — даём понятное имя со временем. */
function named(f: File): File {
  if (!/^image\.(png|jpe?g|gif|webp)$/i.test(f.name)) return f
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  const ext = f.name.split('.').pop()
  return new File([f], `скриншот-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.${ext}`, { type: f.type })
}

function install() {
  if (installed) return
  installed = true
  window.addEventListener('dragenter', (e) => {
    if (!receivers.length || !hasFiles(e)) return
    e.preventDefault()
    depth++
    setDragging(true)
  })
  window.addEventListener('dragover', (e) => {
    if (!receivers.length || !hasFiles(e)) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  })
  window.addEventListener('dragleave', (e) => {
    if (!receivers.length || !hasFiles(e)) return
    depth = Math.max(0, depth - 1)
    if (!depth) setDragging(false)
  })
  window.addEventListener('drop', (e) => {
    if (!receivers.length || !hasFiles(e)) return
    e.preventDefault()
    depth = 0
    setDragging(false)
    const files = Array.from(e.dataTransfer?.files ?? [])
    if (files.length) receivers[receivers.length - 1]!(files)
  })
  window.addEventListener('paste', (e) => {
    if (!receivers.length) return
    const files = Array.from(e.clipboardData?.files ?? [])
    if (!files.length) return
    // вставили только картинку — забираем её; если вместе с текстом, текст вставится как обычно
    if (!e.clipboardData?.getData('text/plain')) e.preventDefault()
    receivers[receivers.length - 1]!(files.map(named))
  })
}

/** Окно принимает файлы, пока смонтировано: перетаскиванием и вставкой. Возвращает «сейчас тащат файл». */
export function useFileDrop(onFiles: (files: File[]) => void): boolean {
  const [dragging, setDrag] = useState(false)
  const cb = useRef(onFiles)
  cb.current = onFiles
  useEffect(() => {
    install()
    const r: Receiver = (files) => cb.current(files)
    receivers.push(r)
    dragListeners.add(setDrag)
    return () => {
      const i = receivers.lastIndexOf(r)
      if (i >= 0) receivers.splice(i, 1)
      dragListeners.delete(setDrag)
    }
  }, [])
  return dragging
}

/** Подсказка поверх окна, пока над ним держат файлы. */
export function DropOverlay({ show, label = 'Отпустите — файлы прикрепятся к задаче' }: { show: boolean; label?: string }) {
  if (!show) return null
  return (
    <div className="backdrop pointer-events-none fixed inset-0 z-[70] flex items-center justify-center p-6 max-md:p-4" aria-hidden="true">
      <div className="flex max-w-full flex-col items-center gap-2 rounded-panel border-2 border-dashed border-accent bg-surface px-10 py-8 text-center shadow-float max-md:px-5 max-md:py-6">
        <Icon name="upload" size={28} className="text-accent" />
        <div className="text-[15px] font-semibold text-fg">{label}</div>
        <div className="text-[12.5px] text-muted">Любые файлы, до 100 МБ каждый</div>
      </div>
    </div>
  )
}

/** Кнопка выбора файлов (несколько сразу). */
export function FilePicker({ onFiles, label = 'Прикрепить файлы', size = 'sm' as const }: { onFiles: (files: File[]) => void; label?: string; size?: 'xs' | 'sm' | 'md' }) {
  const ref = useRef<HTMLInputElement>(null)
  const id = useId()
  return (
    <>
      <input
        id={id}
        ref={ref}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          e.target.value = ''
          if (files.length) onFiles(files)
        }}
      />
      <Button size={size} icon="paperclip" onClick={() => ref.current?.click()}>
        {label}
      </Button>
    </>
  )
}

const isImage = (mime: string) => /^image\//i.test(mime)
/** PDF и текст браузер показывает сам — открываем во вкладке, остальное скачиваем. */
const viewable = (mime: string) => /^application\/pdf$|^text\/plain$/i.test(mime)

/** Файлы, выбранные в форме, но ещё не отправленные. */
export function PendingFiles({ files, progress, onRemove }: { files: File[]; progress?: (number | undefined)[]; onRemove: (index: number) => void }) {
  if (!files.length) return null
  return (
    <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface" aria-label="Прикреплённые файлы">
      {files.map((f, i) => {
        const p = progress?.[i]
        return (
          <li key={`${f.name}-${f.size}-${i}`} className="relative flex min-w-0 items-center gap-2.5 px-3 py-2">
            <Icon name={isImage(f.type) ? 'image' : 'file'} size={15} className="shrink-0 text-subtle" />
            <span className="min-w-0 flex-1 truncate text-[13px] text-fg-2" title={f.name}>
              {f.name}
            </span>
            <span className="tnum shrink-0 text-[11.5px] text-subtle">{formatSize(f.size)}</span>
            {p === undefined ? (
              <button
                type="button"
                className="shrink-0 rounded-ctl p-1 text-subtle hover:bg-surface-2 hover:text-fg"
                onClick={() => onRemove(i)}
                aria-label={`Убрать ${f.name}`}
              >
                <Icon name="x" size={14} />
              </button>
            ) : (
              <span className="tnum w-9 shrink-0 text-right text-[11.5px] text-muted">{Math.round(p * 100)}%</span>
            )}
            {p !== undefined ? (
              <span aria-hidden="true" className="absolute bottom-0 left-0 h-[2px] bg-accent transition-[width]" style={{ width: `${Math.round(p * 100)}%` }} />
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

/** Вложения задачи: картинки — превью, остальное — строки со скачиванием. */
export function AttachmentGrid({ items, taskId, onDelete }: { items: AttachmentInfo[]; taskId: number; onDelete?: (a: AttachmentInfo) => void }) {
  const images = items.filter((a) => isImage(a.mime))
  const others = items.filter((a) => !isImage(a.mime))
  return (
    <div className="flex flex-col gap-2.5">
      {images.length ? (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" aria-label="Изображения">
          {images.map((a) => (
            <li key={a.id} className="group relative min-w-0 overflow-hidden rounded-card border border-line bg-surface-2 shadow-card">
              <a href={`/api/attachments/${a.id}`} target="_blank" rel="noreferrer" className="block" title={a.name}>
                <img src={`/api/attachments/${a.id}`} alt={a.name} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <span className="block truncate border-t border-line bg-surface px-2 py-1 text-[11.5px] text-muted">{a.name}</span>
              </a>
              {onDelete && a.from_task === taskId ? <DeleteButton a={a} onDelete={onDelete} className="absolute top-1 right-1 bg-surface shadow-card" /> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {others.length ? (
        <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface shadow-card" aria-label="Документы">
          {others.map((a) => (
            <li key={a.id} className="flex min-w-0 items-center gap-2.5 px-3 py-2">
              <Icon name="file" size={15} className="shrink-0 text-subtle" />
              <a
                href={viewable(a.mime) ? `/api/attachments/${a.id}` : `/api/attachments/${a.id}?download`}
                {...(viewable(a.mime) ? { target: '_blank', rel: 'noreferrer' } : {})}
                className="min-w-0 flex-1 truncate text-[13px] text-accent hover:underline"
                title={a.name}
              >
                {a.name}
              </a>
              <span className="tnum shrink-0 text-[11.5px] text-subtle">{formatSize(a.size)}</span>
              {a.from_task !== undefined && a.from_task !== taskId ? <span className="shrink-0 text-[11.5px] text-subtle max-[420px]:hidden">из родительской</span> : null}
              {onDelete && a.from_task === taskId ? <DeleteButton a={a} onDelete={onDelete} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function DeleteButton({ a, onDelete, className }: { a: AttachmentInfo; onDelete: (a: AttachmentInfo) => void; className?: string }) {
  return (
    <button
      type="button"
      className={cx('shrink-0 rounded-ctl p-1 text-subtle opacity-80 hover:bg-surface-2 hover:text-bad hover:opacity-100', className)}
      onClick={() => {
        if (window.confirm(`Удалить вложение «${a.name}»?`)) onDelete(a)
      }}
      aria-label={`Удалить ${a.name}`}
    >
      <Icon name="trash" size={13} />
    </button>
  )
}
